import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import LazyLoad from "react-lazy-load";

import { Grid, Box, makeStyles, Typography } from "@material-ui/core";

import {
  NALabel,
  IconTitle,
  MeterPlan,
  NoDataCard,
  ConnectionStatus,
  HighEconomicLabel,
  HighPhysicalLabel,
  GrossRentalRevenues,
  ProfitabilityOverTime,
  UnpaidElectricityBills,
  ProfitabilityComponents,
  PowerConsumptionOverTime,
  NoTableData,
} from "../../../../components/parts";
import {
  powerConsumptionOverTimeAreas,
  getProfitabilityComponentsTableHeader,
  profitabilityComponentsTableTypeName,
  profitabilityComponentsTableRowStyle,
} from "../../constants";
import { UnitAlert } from "./unitAlerts";
import { COLORS } from "../../../../theme/colors";
import { Documents } from "./electricity/documents";
import { Button } from "../../../../components/base";
import { TenancyDataInfo } from "./tenancyData/tenancyDataInfo";
import { TenancyDataGraph } from "./tenancyData/tenancyDataGraph";
import { TenancyDataDetail } from "./tenancyData/tenancyDataDetail";
import TenancyImg from "../../../../assets/images/icons/tenancy.svg";
import BellImg from "../../../../assets/images/icons/bell_black.svg";
import RevenuesImg from "../../../../assets/images/icons/nav/revenues.svg";
import ProfitabilityImg from "../../../../assets/images/icons/profit.svg";
import AttachementImg from "../../../../assets/images/icons/attachement.svg";
import ElectricityImg from "../../../../assets/images/icons/electricity.svg";
import { PowerConsumptionComparison } from "./electricity/powerConsumptionComparison";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { AvgUnrealizedPraasTradingRevenues } from "./unitProfitability/avgUnrealizedPraasTradingRevenues";
import { SupplyRevenuesTotal } from "../properties/electricity/SupplyRevenuesTotal";
import { RevenueBreakdownOverTimeComponent } from "../properties/electricity/RevenueBreakdownOverTimeComponent";
import {
  formatDatetime,
  generateProfitabilityData,
  getData,
  onlyUniqueValuesInObject,
} from "../../../../services";
import { TenantsRebatePolicyModal } from "../../../../components/modals";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  back: {
    color: COLORS.blue,
    paddingRight: 10,
    cursor: "pointer",
  },
}));

export const UnitDetail = ({ building, complex, unit, setUnit, setShowUnitList }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const powerConsumptionOverTimeOptions = ["kWh", currencySymbol];

  const url = `/praas/v2/buildings/${unit.building.id}/properties/units/${unit.id}`;

  const [meterPlan, setMeterPlan] = useState();
  const [connectionStatusData, setConnectionStatusData] = useState();
  const [unpaidData, setUnpaidData] = useState([]);
  const [powerConsumptionOverTime, setPowerConsumptionOverTime] = useState({
    chartData: [],
    previous_year_consumption: 0,
  });
  const [powerConsumptionOverTimeOption, setPowerConsumptionOverTimeOption] = useState(
    powerConsumptionOverTimeOptions[0]
  );
  const [tableBody, setTableBody] = useState([]);
  const [grossRentalRevenueChartData, setGrossRentalRevenueChartData] = useState([]);
  const [profitabilityOverTime, setProfitabilityOverTime] = useState({
    chart_data: [],
    reference_areas: [],
  });
  const [doc, setDoc] = useState();
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    getData(`${url}/electricity-data/`, (data) => {
      setMeterPlan({
        total_meters: data.total_meters,
        meters_owner: data.meters_owner,
      });
      setConnectionStatusData({
        status: data.status,
        since_connected: data.since_connected,
      });
    });

    getData(`${url}/unpaid/`, (data) => {
      if (!onlyUniqueValuesInObject(data, null)) {
        setUnpaidData([{ value: data.electricity_debt }, { value: data.unpaid_bill }]);
      }
    });

    getData(`${url}/profitability-components/`, (data) => {
      setTableBody(
        Object.entries(data).map(([key, value]) => ({
          type: profitabilityComponentsTableTypeName[key],
          rowStyles: profitabilityComponentsTableRowStyle[key],
          ...value,
        }))
      );
    });

    getData(`${url}/rental-revenues/`, (data) => {
      if (!onlyUniqueValuesInObject(data, 0)) {
        const positive = data.rental_revenue + (10 * data.rental_revenue) / 100;
        const totalVacancy = data.economic_vacancy + data.physical_vacancy;
        const negative = totalVacancy + (10 * totalVacancy) / 100;
        setGrossRentalRevenueChartData([
          {
            name: "Page A",
            rental: 0,
            economic_vacancy: 0,
            physical_vacancy: 0,
            positive: positive,
            negative: -negative,
            total_vacancy: 0,
          },
          {
            name: "Page B",
            rental: data.rental_revenue,
            economic_vacancy: -data.economic_vacancy,
            physical_vacancy: -data.physical_vacancy,
            positive: positive,
            negative: -negative,
            total_vacancy: -totalVacancy,
          },
          {
            name: "Page C",
            rental: 0,
            economic_vacancy: 0,
            physical_vacancy: 0,
            positive: positive,
            negative: -negative,
            total_vacancy: 0,
          },
        ]);
      }
    });

    getData(`${url}/profitability-graph/`, (results) => {
      const { chartData, economicXAxisGroups, physicalXAxisGroups, emptyXAxisGroups } =
        generateProfitabilityData(results);

      const referenceAreas = [];
      economicXAxisGroups.forEach((group) => {
        if (group.length >= 2) {
          referenceAreas.push({
            dataKey: "index",
            x1: group[0],
            x2: group[group.length - 1],
            label: <HighEconomicLabel />,
          });
        }
      });
      physicalXAxisGroups.forEach((group) => {
        if (group.length >= 2) {
          referenceAreas.push({
            dataKey: "index",
            x1: group[0],
            x2: group[group.length - 1],
            label: <HighPhysicalLabel />,
          });
        }
      });
      emptyXAxisGroups.forEach((group) => {
        if (group.length > 1) {
          referenceAreas.push({
            dataKey: "index",
            x1: group[0],
            x2: group[group.length - 1],
            label: <NALabel />,
          });
        }
      });

      setProfitabilityOverTime({
        chart_data: chartData,
        reference_areas: referenceAreas,
      });
    });

    getData(`${url}/missing-docs/`, (data) => {
      if (!onlyUniqueValuesInObject(data, null)) setDoc(data);
    });

    return () => {
      setMeterPlan();
      setConnectionStatusData();
      setUnpaidData([]);
      setTableBody([]);
      setGrossRentalRevenueChartData([]);
      setProfitabilityOverTime({
        chart_data: [],
        reference_areas: [],
      });
      setDoc();
    };
  }, []);

  useEffect(() => {
    getData(`${url}/consumption-graph/?type=${powerConsumptionOverTimeOption}`, (data) => {
      const chartData = data.consumption.map((consum) => ({
        x: formatDatetime(new Date(consum.date), "YYYY-M"),
        consumption:
          powerConsumptionOverTimeOption === powerConsumptionOverTimeOptions[0]
            ? consum.kwh
            : consum.price,
        unit:
          powerConsumptionOverTimeOption === powerConsumptionOverTimeOptions[0]
            ? "kwh"
            : currencySymbol,
      }));

      setPowerConsumptionOverTime({
        chartData: chartData,
        previous_year_consumption: data.previous_year_consumption,
      });
    });

    return () =>
      setPowerConsumptionOverTime({
        chartData: [],
        previous_year_consumption: 0,
      });
  }, [powerConsumptionOverTimeOption]);

  return (
    <Box className={classes.root}>
      <Grid
        style={{ marginBottom: 28 }}
        container
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <IconTitle
          iconSrc={TenancyImg}
          title="TENANCY DATA & ANALYTICS"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <Grid container alignItems="center" style={{ width: "fit-content" }}>
          <Typography
            className={classes.back}
            onClick={() => {
              setUnit(null);
            }}
            style={{
              paddingRight: 20,
            }}
          >
            Units list
          </Typography>
          <Typography
            className={classes.back}
            onClick={() => setShowUnitList(false)}
            style={{ borderRight: `1px solid ${COLORS.darks[0][25]}` }}
          >
            Back to Property data
          </Typography>
          <ActionButtonsGroup hideIntegrateBtn />
        </Grid>
      </Grid>
      <TenancyDataInfo unit={unit} />

      <Grid container spacing={3} style={{ marginTop: 30 }}>
        <Grid item xs={4}>
          <TenancyDataDetail unit={unit} />
        </Grid>
        <Grid item xs={8}>
          <TenancyDataGraph unit={unit} />
        </Grid>
      </Grid>

      <IconTitle iconSrc={ElectricityImg} title="ELECTRICITY DATA & ANALYTICS" />

      <Grid container spacing={3} style={{ minHeight: 384 }}>
        <Grid item sm={4}>
          <MeterPlan meterPlan={meterPlan} />
        </Grid>
        <Grid item sm={4}>
          {connectionStatusData?.status ? (
            <ConnectionStatus
              status={connectionStatusData?.status}
              sinceConnected={connectionStatusData?.since_connected}
            />
          ) : (
            <NoDataCard title="Meters PRaaS-Connection Status" />
          )}
        </Grid>
        <Grid item sm={4}>
          <Documents buildingId={unit.building.id} doc={doc} />
        </Grid>
      </Grid>

      <UnpaidElectricityBills type="unit" data={unpaidData} />

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <PowerConsumptionOverTime
            title="Unit Power Consumption Over Time"
            chartData={powerConsumptionOverTime.chartData}
            chartAreas={powerConsumptionOverTimeAreas}
            previousYearConsumption={powerConsumptionOverTime.previous_year_consumption}
            toggleOption={powerConsumptionOverTimeOption}
            toggleOptions={powerConsumptionOverTimeOptions}
            handleChange={setPowerConsumptionOverTimeOption}
          />
        </Grid>
        <Grid item xs={8}>
          <PowerConsumptionComparison unit={unit} />
        </Grid>
      </Grid>

      {building?.id ? (
        <>
          <Grid
            style={{ paddingTop: 25 }}
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <IconTitle
              iconSrc={RevenuesImg}
              title="PRaaS Electricity Supply Revenues"
              rootStyle={{
                marginTop: 0,
                marginBottom: 0,
              }}
            />
            <Box style={{ marginLeft: "auto", height: "fit-content" }}>
              <Button text="TENANTS REBATE POLICY" onClick={() => setOpen(true)} />
            </Box>
            <TenantsRebatePolicyModal
              isOpen={isOpen}
              setOpen={() => setOpen(false)}
              building={building}
            />
          </Grid>
          <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
            <Grid item lg={4}>
              <LazyLoad height="100%">
                <SupplyRevenuesTotal
                  buildingId={building?.id}
                  complexId={complex?.id}
                  title={"Revenues - Total"}
                />
              </LazyLoad>
            </Grid>
            <Grid item lg={8}>
              <LazyLoad height="100%">
                <RevenueBreakdownOverTimeComponent
                  buildingId={building?.id}
                  complexId={complex?.id}
                />
              </LazyLoad>
            </Grid>
          </Grid>
        </>
      ) : null}

      <IconTitle iconSrc={ProfitabilityImg} title="UNIT PROFITABILITY" />

      <ProfitabilityOverTime
        title={`Unit Profitability Over Time (${currencySymbol})`}
        buttonText="Optimize UNIT Profitability"
        onClick={() => null}
        chartData={profitabilityOverTime.chart_data}
        referenceAreas={profitabilityOverTime.reference_areas}
      />

      <ProfitabilityComponents
        title="Unit Profitability Components"
        tableHeader={getProfitabilityComponentsTableHeader(user.unit_system)}
        tableBody={tableBody}
      />

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <GrossRentalRevenues chartData={grossRentalRevenueChartData} />
        </Grid>
        <Grid item xs={8}>
          <AvgUnrealizedPraasTradingRevenues unit={unit} />
        </Grid>
      </Grid>

      <IconTitle iconSrc={BellImg} title="UNIT'S ALERTS" />

      <UnitAlert unit={unit} />

      <IconTitle iconSrc={AttachementImg} title="UNIT'S ATTACHMENTS" />

      <NoTableData title="No data available" />

      {
        // <Grid container spacing={3}>
        //   <Grid item xs={4}>
        //     <Attachement
        //       icon={PdfImg}
        //       ext="PDF"
        //       name="Document long long name comes here.pdf"
        //       date="July 10, 2020"
        //       size="26.9 Mb"
        //     />
        //   </Grid>
        //   <Grid item xs={4}>
        //     <Attachement
        //       icon={DocImg}
        //       ext="DOC"
        //       name="Document long long name comes here.docx"
        //       date="July 10, 2020"
        //       size="26.9 Mb"
        //     />
        //   </Grid>
        // </Grid>
      }
    </Box>
  );
};
